import { useCallback, useEffect } from 'react';
import {
	useLocation,
	useMatch,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import {
	parseModule,
	readModule,
	setModuleParam,
	switchModuleParams,
	withModule,
	type ModuleCode,
} from './modules';
import { HUB_ROUTE, hubPath } from './flow';

/* The hub and every page of a client in one module, below it. */
const CLIENT_MODULE_ROUTE = `${HUB_ROUTE}/*`;

/** The client and module the path names (`/clients/3/modules/h2r/…`), or null
 *  outside a client's module pages. */
function useClientModuleMatch(): {
	clientId: string;
	module: ModuleCode;
} | null {
	const match = useMatch(CLIENT_MODULE_ROUTE);
	if (!match?.params.clientId) return null;
	return {
		clientId: match.params.clientId,
		module: parseModule(match.params.module),
	};
}

/**
 * The current module: from the path under a client's module
 * (`/clients/:id/modules/o2c/…`), from the `module` search param everywhere else.
 *
 * `setModule` is a switch. Under a client it opens the client's hub in the
 * other module; elsewhere it sets `module`, drops the selected group and
 * folder (they belong to the module being left) and keeps every other param.
 * It pushes a history entry, so Back returns to the previous module.
 *
 * `link` adds the current module to an in-app path, so a link keeps it; an
 * old-style intake or audits path comes out under the client and module.
 */
export function useModule(): {
	module: ModuleCode;
	setModule: (module: ModuleCode) => void;
	link: (path: string) => string;
} {
	const [params, setParams] = useSearchParams();
	const scope = useClientModuleMatch();
	const navigate = useNavigate();
	const scopeClient = scope?.clientId;
	const module = scope ? scope.module : readModule(params);
	const setModule = useCallback(
		(next: ModuleCode) =>
			scopeClient
				? navigate(hubPath(scopeClient, next))
				: setParams((current) => switchModuleParams(current, next)),
		[scopeClient, navigate, setParams],
	);
	const link = useCallback(
		(path: string) => withModule(path, module, scopeClient),
		[module, scopeClient],
	);
	return { module, setModule, link };
}

/**
 * A page for one record (an audit, a file) follows that record's module.
 *
 * Opened with another module in the URL — an old link, a typed id — the URL
 * is corrected in place (`replace`, so Back does not return to the wrong
 * one), and the badge and every link on the page then match the record.
 */
export function useFollowRecordModule(
	recordModule: ModuleCode | undefined,
): void {
	const [params, setParams] = useSearchParams();
	const scope = useClientModuleMatch();
	const { pathname, search } = useLocation();
	const navigate = useNavigate();
	const module = scope ? scope.module : readModule(params);
	const inScope = scope !== null;
	useEffect(() => {
		if (!recordModule || recordModule === module) return;
		if (inScope) {
			const moved = pathname.replace(
				/^(\/clients\/[^/]+\/modules\/)[^/]+/,
				`$1${recordModule.toLowerCase()}`,
			);
			navigate(`${moved}${search}`, { replace: true });
		} else {
			setParams((current) => setModuleParam(current, recordModule), {
				replace: true,
			});
		}
	}, [recordModule, module, inScope, pathname, search, navigate, setParams]);
}
